// Gestao de Fotos — pagina dedicada (menu Marketing) pra cuidar do acervo de
// fotos de produto em escala. 4 abas:
//   Acervo      — navegar/pesquisar/renomear/excluir todas as fotos (reusa o picker).
//   Cruzamento  — produtos SEM foto + fotos orfas (arquivo sem produto).
//   Duplicadas  — arquivos de foto identicos, com opcao de consolidar num so.
//   Qualidade   — avaliacao tecnica (ImageMagick) de todas as fotos, em lote.
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Images, ImageOff, Layers, Gauge, Trash2, Search, RefreshCw, AlertTriangle, Check, ExternalLink, Scissors,
} from 'lucide-react';
import { api } from '../../lib/api';
import { useToast } from '../../components/Toast';
import { useDialog } from '../../components/DialogProvider';
import { PickerFotoProduto } from '../produtos/PickerFotoProduto';

type Aba = 'acervo' | 'cruzamento' | 'duplicadas' | 'qualidade';

interface ProdRef { id: number; codigo_interno: string | null; descricao: string }

function kb(bytes: number): string {
  if (!bytes) return '—';
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

export function GestaoFotos() {
  const [aba, setAba] = useState<Aba>('acervo');

  const abas: { id: Aba; rotulo: string; icone: typeof Images }[] = [
    { id: 'acervo', rotulo: 'Acervo', icone: Images },
    { id: 'cruzamento', rotulo: 'Sem foto / Orfas', icone: ImageOff },
    { id: 'duplicadas', rotulo: 'Duplicadas', icone: Layers },
    { id: 'qualidade', rotulo: 'Qualidade', icone: Gauge },
  ];

  return (
    <div>
      <div className="pagina-header">
        <h2 className="pagina-titulo">Gestão de Fotos</h2>
      </div>

      {/* Abas */}
      <div style={{ display: 'flex', gap: 2, borderBottom: '2px solid var(--panel-border, #e5e7eb)', marginBottom: 16 }}>
        {abas.map((t) => {
          const Icone = t.icone;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setAba(t.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '8px 16px',
                border: 'none',
                borderBottom: aba === t.id ? '2px solid var(--tabs-active-fg, var(--primary))' : '2px solid transparent',
                marginBottom: -2,
                background: 'transparent',
                color: aba === t.id ? 'var(--text-color)' : 'var(--text-muted, #6b7280)',
                fontWeight: aba === t.id ? 700 : 500,
                cursor: 'pointer',
                fontSize: 14,
              }}
            >
              <Icone size={16} /> {t.rotulo}
            </button>
          );
        })}
      </div>

      {aba === 'acervo' && <PickerFotoProduto />}
      {aba === 'cruzamento' && <AbaCruzamento />}
      {aba === 'duplicadas' && <AbaDuplicadas />}
      {aba === 'qualidade' && <AbaQualidade />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Aba: Cruzamento — produtos sem foto + arquivos orfaos
// ---------------------------------------------------------------------------
// `nome` vem da foto_metadata: foto batizada de proposito (promovida do Monitor ou nomeada no
// acervo) e NAO e descartavel — so o arquivo anonimo e que e sobra de verdade.
interface Orfa { foto_url: string; bytes: number; nome: string | null }

function AbaCruzamento() {
  const navigate = useNavigate();
  const toast = useToast();
  const dialog = useDialog();
  const [semFoto, setSemFoto] = useState<ProdRef[]>([]);
  const [orfas, setOrfas] = useState<Orfa[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [busca, setBusca] = useState('');
  const [excluindo, setExcluindo] = useState<string | null>(null);

  function carregar() {
    setCarregando(true);
    api
      .get<{ sem_foto: ProdRef[]; orfas: Orfa[] }>('/api/produtos/fotos-cruzamento')
      .then((r) => { setSemFoto(r.sem_foto); setOrfas(r.orfas); })
      .catch((e: any) => toast.erro(e.message ?? 'Falha ao carregar'))
      .finally(() => setCarregando(false));
  }
  useEffect(carregar, []);

  const filtrados = useMemo(() => {
    const t = busca.trim().toLowerCase();
    if (!t) return semFoto;
    return semFoto.filter(
      (p) => (p.codigo_interno ?? '').toLowerCase().includes(t) || p.descricao.toLowerCase().includes(t),
    );
  }, [semFoto, busca]);

  async function excluirOrfa(o: Orfa) {
    // A foto batizada tem um aviso proprio: ela chegou aqui de proposito (alguem a promoveu ou
    // nomeou) e so ainda nao foi vinculada — apagar por engano custa o trabalho de preparar a foto.
    const ok = await dialog.confirmar(
      o.nome
        ? `"${o.nome}"\n\nEsta foto tem nome próprio — foi promovida ou batizada de propósito e está esperando um produto.\n\nExcluir vai apagar o arquivo do disco de vez. Continuar?`
        : `Este arquivo de foto não é usado por nenhum produto.\n\nExcluir vai apagar o arquivo do disco de vez. Continuar?`,
      { titulo: 'Excluir foto órfã', perigoso: true, textoConfirmar: 'Excluir', imagem: o.foto_url },
    );
    if (!ok) return;
    setExcluindo(o.foto_url);
    try {
      await api.post('/api/produtos/foto-excluir', { foto_url: o.foto_url });
      setOrfas((prev) => prev.filter((x) => x.foto_url !== o.foto_url));
      toast.sucesso('Foto órfã excluída');
    } catch (e: any) {
      toast.erro(e.message ?? 'Falha ao excluir');
    } finally {
      setExcluindo(null);
    }
  }

  if (carregando) return <p className="muted">Carregando...</p>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* Produtos sem foto */}
      <section>
        <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 8 }}>
          Produtos sem foto <span className="muted" style={{ fontWeight: 400 }}>({semFoto.length})</span>
        </h3>
        <p className="muted" style={{ fontSize: 13, marginBottom: 10 }}>
          Produtos cadastrados que ainda não têm foto. Clique num produto pra abrir o cadastro e adicionar.
        </p>
        {semFoto.length === 0 ? (
          <p className="muted" style={{ padding: 12 }}>Todos os produtos têm foto. 🎉</p>
        ) : (
          <>
            <div style={{ position: 'relative', maxWidth: 420, marginBottom: 10 }}>
              <Search size={16} style={{ position: 'absolute', left: 10, top: 11, color: 'var(--text-muted, #6b7280)' }} />
              <input
                type="text"
                placeholder="Filtrar por código ou descrição..."
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                style={{
                  width: '100%', padding: '9px 10px 9px 34px', borderRadius: 6,
                  border: '1px solid var(--input-border, #d1d5db)', background: 'var(--input-bg)',
                  color: 'var(--text-color)', fontSize: 14,
                }}
              />
            </div>
            <div style={{ maxHeight: '52vh', overflowY: 'auto', border: '1px solid var(--panel-border, #e5e7eb)', borderRadius: 8 }}>
              {filtrados.map((p) => (
                <div
                  key={p.id}
                  onClick={() => navigate(`/produtos/${p.id}`)}
                  title="Abrir cadastro do produto"
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '7px 10px',
                    borderBottom: '1px solid var(--grid-row-border, #f3f4f6)', cursor: 'pointer', fontSize: 13,
                  }}
                >
                  <span style={{ fontWeight: 700, color: 'var(--text-muted, #6b7280)', minWidth: 56 }}>
                    {p.codigo_interno ?? '—'}
                  </span>
                  <span style={{ flex: 1, minWidth: 0 }}>{p.descricao}</span>
                  <ExternalLink size={14} style={{ color: 'var(--text-muted, #9ca3af)', flexShrink: 0 }} />
                </div>
              ))}
              {filtrados.length === 0 && (
                <p className="muted" style={{ padding: 12 }}>Nenhum produto pra esse filtro.</p>
              )}
            </div>
          </>
        )}
      </section>

      {/* Fotos orfas */}
      <section>
        <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 8 }}>
          Fotos órfãs <span className="muted" style={{ fontWeight: 400 }}>({orfas.length})</span>
          {orfas.some((o) => o.nome) && (
            <span className="muted" style={{ fontWeight: 400, fontSize: 13 }}>
              {' '}· {orfas.filter((o) => o.nome).length} com nome
            </span>
          )}
        </h3>
        <p className="muted" style={{ fontSize: 13, marginBottom: 10, lineHeight: 1.5 }}>
          Arquivos de foto no servidor que nenhum produto usa. <b>Nem toda foto aqui é descartável</b> — as
          marcadas <span style={{
            background: '#fde68a', color: '#000', fontSize: 10, fontWeight: 800, letterSpacing: 0.4,
            padding: '2px 7px', borderRadius: 999,
          }}>TEM NOME</span> foram batizadas de propósito (promovidas do Monitor de Custos ou nomeadas por
          você) e estão esperando um produto. As sem nome ocupam espaço à toa e podem ser excluídas.
        </p>
        {orfas.length === 0 ? (
          <p className="muted" style={{ padding: 12 }}>Nenhuma foto órfã. O acervo está limpo. 🎉</p>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 12 }}>
            {orfas.map((o) => (
              <div
                key={o.foto_url}
                style={{
                  display: 'flex', flexDirection: 'column', gap: 4, padding: 6,
                  border: '1px solid var(--panel-border, #e5e7eb)', borderRadius: 8,
                  background: 'var(--input-bg, #fff)', opacity: excluindo === o.foto_url ? 0.5 : 1,
                }}
              >
                <div style={{ position: 'relative' }}>
                  <img
                    src={o.foto_url}
                    alt=""
                    loading="lazy"
                    style={{
                      width: '100%', aspectRatio: '1', objectFit: 'contain', background: '#fff',
                      borderRadius: 4, border: '1px solid var(--panel-border, #e5e7eb)',
                    }}
                  />
                  {o.nome && (
                    <span
                      title="Foto batizada de propósito — está esperando um produto, não é sobra"
                      style={{
                        position: 'absolute', top: 4, left: 4,
                        background: '#fde68a', color: '#000', fontSize: 9, fontWeight: 800,
                        letterSpacing: 0.5, padding: '2px 6px', borderRadius: 999,
                      }}
                    >
                      TEM NOME
                    </span>
                  )}
                </div>
                {o.nome && (
                  <span style={{
                    fontSize: 12, lineHeight: 1.3, fontWeight: 600, color: 'var(--text-color)',
                    overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                  }}>
                    {o.nome}
                  </span>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--text-muted, #6b7280)' }}>
                  <span style={{ flex: 1 }}>{kb(o.bytes)}</span>
                  <button
                    type="button"
                    onClick={() => excluirOrfa(o)}
                    disabled={excluindo === o.foto_url}
                    title="Excluir este arquivo órfão"
                    style={{ background: 'transparent', border: 'none', color: '#dc2626', cursor: 'pointer', padding: 2 }}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Aba: Duplicadas — arquivos identicos
// ---------------------------------------------------------------------------
interface ArquivoDup { foto_url: string; bytes: number; produtos: ProdRef[] }
interface GrupoDup { hash: string; arquivos: ArquivoDup[] }

function AbaDuplicadas() {
  const toast = useToast();
  const dialog = useDialog();
  const [grupos, setGrupos] = useState<GrupoDup[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [consolidando, setConsolidando] = useState<string | null>(null);

  function carregar() {
    setCarregando(true);
    api
      .get<GrupoDup[]>('/api/produtos/fotos-duplicadas')
      .then(setGrupos)
      .catch((e: any) => toast.erro(e.message ?? 'Falha ao carregar'))
      .finally(() => setCarregando(false));
  }
  useEffect(carregar, []);

  async function consolidar(grupo: GrupoDup, manter: ArquivoDup) {
    const remover = grupo.arquivos.filter((a) => a.foto_url !== manter.foto_url);
    const totalProds = remover.reduce((s, a) => s + a.produtos.length, 0);
    const ok = await dialog.confirmar(
      `Os ${grupo.arquivos.length} arquivos deste grupo são idênticos.\n\n` +
        `Vou apontar ${totalProds} produto(s) das outras cópias pra esta foto e apagar ${remover.length} arquivo(s) repetido(s). Continuar?`,
      { titulo: 'Consolidar fotos duplicadas', textoConfirmar: 'Consolidar', imagem: manter.foto_url },
    );
    if (!ok) return;
    setConsolidando(grupo.hash);
    try {
      const r = await api.post<{ produtos_movidos: number; arquivos_apagados: number }>(
        '/api/produtos/fotos-consolidar',
        { manter: manter.foto_url, remover: remover.map((a) => a.foto_url) },
      );
      toast.sucesso(`${r.produtos_movidos} produto(s) reaproveitados · ${r.arquivos_apagados} arquivo(s) apagado(s)`);
      setGrupos((prev) => prev.filter((g) => g.hash !== grupo.hash));
    } catch (e: any) {
      toast.erro(e.message ?? 'Falha ao consolidar');
    } finally {
      setConsolidando(null);
    }
  }

  if (carregando) return <p className="muted">Procurando duplicadas...</p>;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
        <p className="muted" style={{ fontSize: 13, flex: 1 }}>
          Grupos de arquivos com conteúdo <strong>idêntico</strong>. Consolidar deixa um único arquivo e aponta todos
          os produtos pra ele — economiza espaço e facilita a manutenção.
        </p>
        <button type="button" className="btn-secundario" onClick={carregar}>
          <RefreshCw size={14} style={{ display: 'inline', marginRight: 4 }} /> Reanalisar
        </button>
      </div>

      {grupos.length === 0 ? (
        <p className="muted" style={{ padding: 16 }}>Nenhuma foto duplicada encontrada. 🎉</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {grupos.map((g, gi) => (
            <div key={g.hash} style={{ border: '1px solid var(--panel-border, #e5e7eb)', borderRadius: 10, padding: 12 }}>
              <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 10 }}>
                Grupo {gi + 1} — {g.arquivos.length} arquivos idênticos
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12 }}>
                {g.arquivos.map((a) => (
                  <div
                    key={a.foto_url}
                    style={{
                      display: 'flex', flexDirection: 'column', gap: 6, padding: 8,
                      border: '1px solid var(--panel-border, #e5e7eb)', borderRadius: 8, background: 'var(--input-bg, #fff)',
                    }}
                  >
                    <div style={{ display: 'flex', gap: 8 }}>
                      <img
                        src={a.foto_url}
                        alt=""
                        loading="lazy"
                        style={{
                          width: 72, height: 72, objectFit: 'contain', background: '#fff',
                          borderRadius: 4, border: '1px solid var(--panel-border, #e5e7eb)', flexShrink: 0,
                        }}
                      />
                      <div style={{ flex: 1, minWidth: 0, fontSize: 11, color: 'var(--text-muted, #6b7280)' }}>
                        <div>{kb(a.bytes)} · {a.produtos.length} produto{a.produtos.length === 1 ? '' : 's'}</div>
                        <div style={{ marginTop: 4, maxHeight: 78, overflowY: 'auto' }}>
                          {a.produtos.map((p) => (
                            <div key={p.id} style={{ lineHeight: 1.35 }}>
                              <strong>#{p.id}</strong> {p.codigo_interno || '—'} — {p.descricao}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn-secundario"
                      onClick={() => consolidar(g, a)}
                      disabled={consolidando === g.hash}
                      title="Manter esta foto e apontar todos os produtos das cópias pra ela"
                      style={{ fontSize: 12, padding: '5px 8px' }}
                    >
                      <Layers size={13} style={{ display: 'inline', marginRight: 4 }} />
                      {consolidando === g.hash ? 'Consolidando...' : 'Consolidar nesta'}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Aba: Qualidade — avaliacao tecnica em lote
// ---------------------------------------------------------------------------
interface TecnicoResumo {
  largura: number; altura: number; formato: string; bytes: number; otimizavel: boolean; problemas: string[];
}
// Regua do master (Biblioteca de Fotos, Fase 2): >=2000px / quadrado / transparente.
interface MasterResumo {
  veredito: 'completo' | 'parcial' | 'ausente';
  master_url: string | null;
  largura: number; altura: number;
  alta_resolucao: boolean; quadrado: boolean; transparente: boolean;
  problemas: string[];
}
interface AvalLote { foto_url: string; veredito: string; tecnico: TecnicoResumo | null; master: MasterResumo }
interface GaleriaFoto {
  foto_url: string; nome: string | null; bytes: number; usos: number; produtos: ProdRef[];
}

const CHUNK_AVALIA = 25;

const CHUNK_MASTER = 8;

function AbaQualidade() {
  const navigate = useNavigate();
  const toast = useToast();
  const dialog = useDialog();
  const [galeria, setGaleria] = useState<GaleriaFoto[]>([]);
  const [resultados, setResultados] = useState<Map<string, AvalLote>>(new Map());
  const [avaliando, setAvaliando] = useState(false);
  const [progresso, setProgresso] = useState<{ feito: number; total: number } | null>(null);
  const [soRefazer, setSoRefazer] = useState(false);
  const [soSemMaster, setSoSemMaster] = useState(false);
  // Geracao de master em lote (Biblioteca de Fotos, Fase 3).
  const [semMasterCount, setSemMasterCount] = useState<number | null>(null);
  const [gerandoMaster, setGerandoMaster] = useState(false);
  const [progressoMaster, setProgressoMaster] = useState<{ feito: number; total: number; gerados: number; erros: number } | null>(null);

  // Quantas fotos ainda nao tem master — pra rotular o botao de geracao.
  function carregarSemMaster() {
    api
      .get<{ foto_urls: string[] }>('/api/produtos/fotos-sem-master')
      .then((r) => setSemMasterCount(r.foto_urls.length))
      .catch(() => setSemMasterCount(null));
  }
  useEffect(carregarSemMaster, []);

  // Gera o master de todas as fotos sem master, recortando o fundo via IA.
  async function gerarMastersEmLote() {
    let lista: string[];
    try {
      const r = await api.get<{ foto_urls: string[] }>('/api/produtos/fotos-sem-master');
      lista = r.foto_urls;
    } catch (e: any) {
      toast.erro(e.message ?? 'Falha ao listar fotos sem master');
      return;
    }
    if (lista.length === 0) {
      toast.sucesso('Todas as fotos já têm master. 🎉');
      setSemMasterCount(0);
      return;
    }
    const minutos = Math.max(1, Math.round((lista.length * 2.5) / 60));
    const ok = await dialog.confirmar(
      `${lista.length} foto(s) ainda não têm master.\n\n` +
        `Vou gerar o master de cada uma recortando o fundo com IA. Roda no servidor e pode levar cerca de ${minutos} min — ` +
        `deixe esta aba aberta. Pode parar e retomar depois: o que já foi gerado não é refeito.\n\nContinuar?`,
      { titulo: 'Gerar masters em lote', textoConfirmar: 'Gerar' },
    );
    if (!ok) return;
    setGerandoMaster(true);
    setProgressoMaster({ feito: 0, total: lista.length, gerados: 0, erros: 0 });
    let gerados = 0;
    let erros = 0;
    for (let i = 0; i < lista.length; i += CHUNK_MASTER) {
      const bloco = lista.slice(i, i + CHUNK_MASTER);
      try {
        const r = await api.post<{ status: string }[]>('/api/produtos/fotos-gerar-master-lote', { foto_urls: bloco });
        for (const x of r) {
          if (x.status === 'gerado') gerados++;
          else if (x.status === 'erro') erros++;
        }
      } catch {
        erros += bloco.length;
      }
      setProgressoMaster({ feito: Math.min(i + CHUNK_MASTER, lista.length), total: lista.length, gerados, erros });
    }
    setGerandoMaster(false);
    setProgressoMaster(null);
    setSemMasterCount(Math.max(0, lista.length - gerados));
    toast.sucesso(
      `Masters gerados: ${gerados}.${erros > 0 ? ` Falhas: ${erros} (pode rodar de novo pra tentar).` : ''}` +
        ' Clique em "Avaliar todas as fotos" pra ver os vereditos atualizados.',
    );
  }

  async function avaliar() {
    setAvaliando(true);
    setResultados(new Map());
    try {
      const lista = await api.get<GaleriaFoto[]>('/api/produtos/fotos-galeria');
      setGaleria(lista);
      setProgresso({ feito: 0, total: lista.length });
      const acc = new Map<string, AvalLote>();
      for (let i = 0; i < lista.length; i += CHUNK_AVALIA) {
        const bloco = lista.slice(i, i + CHUNK_AVALIA).map((g) => g.foto_url);
        try {
          const r = await api.post<AvalLote[]>('/api/produtos/fotos-avaliar-lote', { foto_urls: bloco });
          r.forEach((x) => acc.set(x.foto_url, x));
          setResultados(new Map(acc));
        } catch {
          /* falha num bloco nao derruba o lote */
        }
        setProgresso({ feito: Math.min(i + CHUNK_AVALIA, lista.length), total: lista.length });
      }
      toast.sucesso('Avaliação concluída');
    } catch (e: any) {
      toast.erro(e.message ?? 'Falha ao avaliar');
    } finally {
      setAvaliando(false);
      setProgresso(null);
    }
  }

  const galeriaPorUrl = useMemo(() => {
    const m = new Map<string, GaleriaFoto>();
    galeria.forEach((g) => m.set(g.foto_url, g));
    return m;
  }, [galeria]);

  const avaliados = [...resultados.values()];
  const refazer = avaliados.filter((a) => a.veredito === 'refazer').length;
  const boas = avaliados.filter((a) => a.veredito === 'boa').length;
  const masterCompleto = avaliados.filter((a) => a.master?.veredito === 'completo').length;
  const masterParcial = avaliados.filter((a) => a.master?.veredito === 'parcial').length;
  const masterAusente = avaliados.filter((a) => !a.master || a.master.veredito === 'ausente').length;
  const visiveis = avaliados.filter((a) => {
    if (soRefazer && a.veredito !== 'refazer') return false;
    if (soSemMaster && a.master?.veredito === 'completo') return false;
    return true;
  });

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
        <p className="muted" style={{ fontSize: 13, flex: 1, minWidth: 240 }}>
          Avalia cada foto em duas camadas: a <strong>foto visível</strong> (resolução, formato, proporção, peso —
          marca <strong>refazer</strong> quando está abaixo do mínimo das redes sociais) e o <strong>master</strong> da
          Biblioteca de Fotos — a fonte arquivada, que deve ser <strong>≥2000px, quadrada e de fundo transparente</strong>.
        </p>
        <button
          type="button"
          className="btn-secundario"
          onClick={gerarMastersEmLote}
          disabled={avaliando || gerandoMaster || semMasterCount === 0}
          title="Recorta o fundo de cada foto sem master e arquiva o master da Biblioteca de Fotos"
        >
          <Scissors size={14} style={{ display: 'inline', marginRight: 4 }} />
          {gerandoMaster
            ? `Gerando ${progressoMaster?.feito ?? 0}/${progressoMaster?.total ?? 0}...`
            : semMasterCount === 0
              ? 'Todas com master'
              : `Gerar masters em lote${semMasterCount != null ? ` (${semMasterCount} sem master)` : ''}`}
        </button>
        <button type="button" className="btn-primario" onClick={avaliar} disabled={avaliando || gerandoMaster}>
          <Gauge size={14} style={{ display: 'inline', marginRight: 4 }} />
          {avaliando ? `Avaliando ${progresso?.feito ?? 0}/${progresso?.total ?? 0}...` : 'Avaliar todas as fotos'}
        </button>
      </div>

      {progressoMaster && (
        <div style={{ fontSize: 13, marginBottom: 12, color: 'var(--text-muted, #6b7280)' }}>
          Gerando masters: <strong>{progressoMaster.feito}/{progressoMaster.total}</strong>
          {' · '}<strong style={{ color: '#16a34a' }}>{progressoMaster.gerados}</strong> gerados
          {progressoMaster.erros > 0 && <>{' · '}<strong style={{ color: '#dc2626' }}>{progressoMaster.erros}</strong> falhas</>}
          {' — recortando o fundo com IA, pode levar alguns minutos.'}
        </div>
      )}

      {avaliados.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 13, marginBottom: 12 }}>
          <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontWeight: 700, minWidth: 86 }}>Foto visível:</span>
            <span><strong style={{ color: '#16a34a' }}>{boas}</strong> boas</span>
            <span><strong style={{ color: '#dc2626' }}>{refazer}</strong> pra refazer</span>
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}>
              <input type="checkbox" checked={soRefazer} onChange={(e) => setSoRefazer(e.target.checked)} />
              Mostrar só as que precisam refazer
            </label>
          </div>
          <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontWeight: 700, minWidth: 86 }}>Master:</span>
            <span><strong style={{ color: '#16a34a' }}>{masterCompleto}</strong> completo</span>
            <span><strong style={{ color: '#b45309' }}>{masterParcial}</strong> parcial</span>
            <span><strong style={{ color: '#6b7280' }}>{masterAusente}</strong> sem master</span>
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}>
              <input type="checkbox" checked={soSemMaster} onChange={(e) => setSoSemMaster(e.target.checked)} />
              Mostrar só sem master ideal
            </label>
          </div>
        </div>
      )}

      {avaliados.length === 0 ? (
        <p className="muted" style={{ padding: 16 }}>
          Clique em <strong>"Avaliar todas as fotos"</strong> pra rodar a análise.
        </p>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
          {visiveis.map((a) => {
            const g = galeriaPorUrl.get(a.foto_url);
            const refaz = a.veredito === 'refazer';
            return (
              <div
                key={a.foto_url}
                style={{
                  display: 'flex', gap: 10, padding: 8,
                  border: `1px solid ${refaz ? '#fca5a5' : 'var(--panel-border, #e5e7eb)'}`,
                  borderRadius: 8, background: 'var(--input-bg, #fff)',
                }}
              >
                <img
                  src={a.foto_url}
                  alt=""
                  loading="lazy"
                  style={{
                    width: 84, height: 84, objectFit: 'contain', background: '#fff',
                    borderRadius: 4, border: '1px solid var(--panel-border, #e5e7eb)', flexShrink: 0,
                  }}
                />
                <div style={{ flex: 1, minWidth: 0, fontSize: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 700 }}>
                    {refaz ? (
                      <><AlertTriangle size={14} color="#dc2626" /> <span style={{ color: '#dc2626' }}>Refazer</span></>
                    ) : (
                      <><Check size={14} color="#16a34a" /> <span style={{ color: '#16a34a' }}>Boa</span></>
                    )}
                  </div>
                  {a.tecnico && (
                    <div style={{ color: 'var(--text-muted, #6b7280)', marginTop: 2 }}>
                      {a.tecnico.largura}×{a.tecnico.altura} · {a.tecnico.formato} · {kb(a.tecnico.bytes)}
                    </div>
                  )}
                  {a.tecnico && a.tecnico.problemas.length > 0 && (
                    <ul style={{ margin: '4px 0 0', paddingLeft: 16, color: '#b45309', lineHeight: 1.35 }}>
                      {a.tecnico.problemas.map((p, i) => <li key={i}>{p}</li>)}
                    </ul>
                  )}
                  {/* Regua do master (Biblioteca de Fotos, Fase 2) */}
                  <div style={{ marginTop: 6, paddingTop: 6, borderTop: '1px solid var(--grid-row-border, #f3f4f6)' }}>
                    {(a.master?.veredito ?? 'ausente') === 'completo' && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 700, color: '#16a34a' }}>
                        <Check size={13} /> Master completo
                        <span style={{ fontWeight: 400, color: 'var(--text-muted, #6b7280)' }}>
                          {a.master.largura}×{a.master.altura}
                        </span>
                      </div>
                    )}
                    {a.master?.veredito === 'parcial' && (
                      <>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 700, color: '#b45309' }}>
                          <AlertTriangle size={13} /> Master parcial
                        </div>
                        {a.master.problemas.length > 0 && (
                          <ul style={{ margin: '3px 0 0', paddingLeft: 16, color: '#b45309', lineHeight: 1.35 }}>
                            {a.master.problemas.map((p, i) => <li key={i}>{p}</li>)}
                          </ul>
                        )}
                      </>
                    )}
                    {(a.master?.veredito ?? 'ausente') === 'ausente' && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 700, color: '#6b7280' }}>
                        <ImageOff size={13} /> Sem master
                        {a.master?.problemas?.[0] && (
                          <span style={{ fontWeight: 400 }}>— {a.master.problemas[0]}</span>
                        )}
                      </div>
                    )}
                  </div>
                  {g && g.produtos.length > 0 && (
                    <button
                      type="button"
                      onClick={() => navigate(`/produtos/${g.produtos[0]!.id}`)}
                      title="Abrir o cadastro do produto"
                      style={{
                        marginTop: 6, background: 'transparent', border: 'none', padding: 0,
                        color: 'var(--primary)', cursor: 'pointer', fontSize: 12, textDecoration: 'underline',
                        textDecorationStyle: 'dotted', textAlign: 'left',
                      }}
                    >
                      {g.produtos.length === 1
                        ? `#${g.produtos[0]!.id} ${g.produtos[0]!.codigo_interno || ''} — abrir produto`
                        : `${g.produtos.length} produtos usam — abrir o 1º`}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
